import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    Bot,
    PenSquare,
    Users,
    CheckSquare,
    Store,
    Image,
    Camera,
    DollarSign,
    Sparkles,
    Calendar,
    UtensilsCrossed,
    Settings,
    Grid3X3,
    X
} from 'lucide-react';

interface NavItem {
    id: string;
    label: string;
    icon: React.ComponentType<any>;
    path: string;
}

interface NavGroup {
    groupLabel: string;
    items: NavItem[];
}

// Always-visible bottom tabs on mobile (4 primary + More button = 5 slots)
const PRIMARY_NAV: NavItem[] = [
    { id: 'overview',  label: 'Overview', icon: LayoutDashboard, path: '/admin/dashboard' },
    { id: 'guests',    label: 'Guests',   icon: Users,           path: '/admin/guests'    },
    { id: 'tasks',     label: 'Tasks',    icon: CheckSquare,     path: '/admin/tasks'     },
    { id: 'gallery',   label: 'Gallery',  icon: Image,           path: '/admin/gallery'   },
];

// Grouped items shown in the "More" bottom sheet
const MORE_NAV_GROUPS: NavGroup[] = [
    {
        groupLabel: '🗓 Planning',
        items: [
            { id: 'planner',  label: 'Mahurat',  icon: Sparkles,        path: '/admin/wedding-planner' },
            { id: 'schedule', label: 'Schedule', icon: Calendar,        path: '/admin/ceremonies'      },
            { id: 'budget',   label: 'Budget',   icon: DollarSign,      path: '/budget'                },
            { id: 'menu',     label: 'Menu',     icon: UtensilsCrossed, path: '/admin/menu'            },
        ],
    },
    {
        groupLabel: '🤝 Connect',
        items: [
            { id: 'shadiwala',     label: 'Shadi Wala',  icon: Bot,      path: '/admin/shadi-wala'     },
            { id: 'create-post',   label: 'Create Post', icon: PenSquare, path: '/admin/create-post'   },
            { id: 'vendors',       label: 'Vendors',     icon: Store,     path: '/admin/vendors'        },
            { id: 'photographers', label: 'Experts',     icon: Camera,    path: '/admin/photographers' },
        ],
    },
    {
        groupLabel: '⚙️ Account',
        items: [
            { id: 'settings', label: 'Settings', icon: Settings, path: '/admin/settings' },
        ],
    },
];

// All items flat — used for desktop sidebar
const ALL_NAV: NavItem[] = [
    ...PRIMARY_NAV,
    ...MORE_NAV_GROUPS.flatMap(g => g.items),
];

const AdminSidebar: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const [moreOpen, setMoreOpen] = useState(false);

    const isActive = (path: string) => {
        if (path === '/admin/dashboard') return location.pathname === path;
        return location.pathname.startsWith(path);
    };

    const navigate = (path: string) => {
        history.push(path);
        setMoreOpen(false);
    };

    // Check if any "more" item is active (to highlight the More button)
    const isMoreActive = MORE_NAV_GROUPS.flatMap(g => g.items).some(item => isActive(item.path));

    return (
        <>
            {/* ── DESKTOP SIDEBAR (≥768px): all items vertical ── */}
            <nav className="admin-agency-navbar admin-sidebar-desktop">
                <div className="admin-agency-nav-items">
                    {ALL_NAV.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.path);
                        return (
                            <button
                                key={item.id}
                                onClick={() => navigate(item.path)}
                                className={`admin-agency-nav-btn ${active ? 'active' : ''}`}
                                title={item.label}
                            >
                                <Icon size={20} />
                                <span>{item.label}</span>
                            </button>
                        );
                    })}
                </div>
            </nav>

            {/* ── MOBILE BOTTOM BAR (< 768px): 5 slots ── */}
            <nav className="admin-agency-navbar admin-bottom-bar-mobile">
                <div className="admin-agency-nav-items admin-bottom-nav-items">
                    {PRIMARY_NAV.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item.path);
                        return (
                            <button
                                key={item.id}
                                onClick={() => navigate(item.path)}
                                className={`admin-agency-nav-btn ${active ? 'active' : ''}`}
                                title={item.label}
                            >
                                <Icon size={20} />
                                <span>{item.label}</span>
                            </button>
                        );
                    })}
                    {/* More button */}
                    <button
                        onClick={() => setMoreOpen(true)}
                        className={`admin-agency-nav-btn ${isMoreActive || moreOpen ? 'active' : ''}`}
                        title="More"
                    >
                        <Grid3X3 size={20} />
                        <span>More</span>
                    </button>
                </div>
            </nav>

            {/* ── MORE BOTTOM SHEET ── */}
            {moreOpen && (
                <div className="admin-more-overlay" onClick={() => setMoreOpen(false)}>
                    <div className="admin-more-sheet" onClick={e => e.stopPropagation()}>
                        {/* Sheet handle */}
                        <div className="admin-more-handle" />

                        {/* Close button */}
                        <button className="admin-more-close" onClick={() => setMoreOpen(false)}>
                            <X size={18} />
                        </button>

                        {/* Grouped items */}
                        <div className="admin-more-content">
                            {MORE_NAV_GROUPS.map((group) => (
                                <div key={group.groupLabel} className="admin-more-group">
                                    <p className="admin-more-group-label">{group.groupLabel}</p>
                                    <div className="admin-more-group-items">
                                        {group.items.map((item) => {
                                            const Icon = item.icon;
                                            const active = isActive(item.path);
                                            return (
                                                <button
                                                    key={item.id}
                                                    onClick={() => navigate(item.path)}
                                                    className={`admin-more-item ${active ? 'active' : ''}`}
                                                    title={item.label}
                                                >
                                                    <div className="admin-more-item-icon">
                                                        <Icon size={22} />
                                                    </div>
                                                    <span>{item.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AdminSidebar;
